/**
 * Both admin banners must drive their backend procedure to completion rather
 * than firing once and leaving a stale count on screen. The two loops below are
 * the small, testable cores of that behaviour.
 */

type CancelResumeResult = { refundedCount: number; pendingRefundCount: number }

type Lotish = {
	state: string
	initiatorUserId: string | null
	initiatorNotifiedAt?: Date | undefined
}

/** The initiator-not-notified banner shows only on a draft lot that has an
 * initiator who has never been reached. */
export function shouldShowInitiatorBanner(lot: Lotish): boolean {
	return (
		lot.state === 'draft' &&
		Boolean(lot.initiatorUserId) &&
		!lot.initiatorNotifiedAt
	)
}

/**
 * `CancelOrResume` flips the lot on the first call, then refunds a bounded batch
 * and returns how many buyers remain. Keep calling it until nothing is pending.
 * Bail out if a call makes no progress (refunded nobody yet still reports work
 * left), so a persistent backend error cannot spin forever.
 */
export async function drainCancellation(
	lotId: string,
	runOnce: (lotId: string) => Promise<CancelResumeResult>,
	options?: { onProgress?: (pendingRefundCount: number) => void; maxCalls?: number },
): Promise<number> {
	const maxCalls = options?.maxCalls ?? 1000
	let pendingRefundCount = Number.POSITIVE_INFINITY
	for (let call = 0; call < maxCalls; call++) {
		const result = await runOnce(lotId)
		pendingRefundCount = result.pendingRefundCount
		options?.onProgress?.(pendingRefundCount)
		if (pendingRefundCount <= 0) break
		if (result.refundedCount <= 0) break
	}
	return pendingRefundCount
}

/**
 * `RetryInitiatorNotification` re-sends the draft deep-link and stamps
 * `initiatorNotifiedAt` only when Telegram accepted it. Retry until it reports
 * success, bounded so a buyer who never opened the bot does not cause an endless
 * loop of sends.
 */
export async function drainInitiatorNotification(
	id: string,
	runOnce: (id: string) => Promise<{ notified: boolean }>,
	options?: { maxAttempts?: number },
): Promise<boolean> {
	const maxAttempts = options?.maxAttempts ?? 3
	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const result = await runOnce(id)
		if (result.notified) return true
	}
	return false
}
